if (!customElements.get('cro-header')) {
  let selectedEditorBlock = null;
  customElements.define('cro-header', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.observer?.disconnect();
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.toggle = this.querySelector('[data-cro-toggle]');
      this.backdrop = this.querySelector('[data-cro-backdrop]');
      this.desktop = window.matchMedia('(min-width: 1100px)');
      this.editorMode = Boolean(window.Shopify?.designMode);
      this.editorPinned = false;
      // Vision uses this state for collection filters and sticky product galleries.
      document.body.classList.toggle('header-sticky--active', this.dataset.sticky === 'true');
      this.applyNestedConfiguration();
      this.setupSearch(options);
      this.setupCategory(options);
      this.setupLocalization(options);
      this.classList.add('cro-ready');
      this.toggle.addEventListener('click', () => {
        const open = !this.hasAttribute('data-nav-open');
        this.closeMenus();
        this.toggleAttribute('data-nav-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));
        this.updateOverlay();
      }, options);
      this.querySelector('[data-cro-nav-close]').addEventListener('click', () => {
        this.closeMenus();
        this.toggle.focus();
      }, options);
      this.querySelectorAll('[data-cro-menu-back]').forEach(button => {
        button.addEventListener('click', () => this.backToMenu(button.closest('[data-cro-menu]')), options);
      });
      this.querySelectorAll('[data-cro-menu], [data-cro-group]').forEach(details => {
        details.addEventListener('toggle', () => {
          if (!details.open) { this.updateOverlay(); return; }
          const group = details.hasAttribute('data-cro-group');
          if (!group) {
            this.closeEditorProducts();
            this.closeLocalization();
            this.closeSuggestions();
          }
          if (group && !this.desktop.matches) return;
          const selector = group ? '[data-cro-group]' : '[data-cro-menu]';
          details.parentElement.querySelectorAll(':scope > ' + selector).forEach(sibling => {
            if (sibling !== details) sibling.open = false;
          });
          if (!group && this.desktop.matches) {
            const groups = [...details.querySelectorAll('[data-cro-group]')];
            const active = groups.find(item => item.open) || groups[0];
            groups.forEach(item => { item.open = item === active; });
          }
          if (!group && !this.desktop.matches && this.dataset.mobileMenuStyle === 'featured') {
            this.querySelector('[data-cro-nav]').scrollTop = 0;
            if (document.activeElement === details.querySelector('summary')) {
              details.querySelector('[data-cro-menu-back]')?.focus({ preventScroll: true });
            }
          }
          this.updateOverlay();
        }, options);
        details.addEventListener('pointerenter', event => {
          if (this.editorPinned) return;
          if (event.pointerType !== 'mouse' || !this.desktop.matches || this.hasAttribute('data-search-open')) return;
          clearTimeout(this.menuTimer);
          if (this.dataset.menuHover !== 'true') return;
          details.open = true;
        }, options);
      });
      const dismissNavigation = event => {
        if (this.editorPinned) return;
        if (event.pointerType !== 'mouse' || !this.desktop.matches || this.hasAttribute('data-search-open')) return;
        clearTimeout(this.menuTimer);
        this.menuTimer = setTimeout(() => this.closeNavigation(), 250);
      };
      this.addEventListener('pointerleave', dismissNavigation, options);
      this.backdrop.addEventListener('pointerenter', dismissNavigation, options);
      this.addEventListener('pointerenter', () => clearTimeout(this.menuTimer), options);
      this.querySelectorAll('.cro-navigation__desktop > .cro-navigation__link').forEach(link => {
        link.addEventListener('pointerenter', event => {
          if (!this.editorPinned && event.pointerType === 'mouse') this.closeNavigation();
        }, options);
      });
      this.addEventListener('keydown', event => {
        if (event.target.closest('[data-cro-account]') && this.accountEscape) return;
        if (event.key !== 'Escape') return;
        this.handledEscape = false;
        const openMenu = this.querySelector('[data-cro-menu][open]');
        const openCountry = this.querySelector('[data-cro-localization][open]');
        const editorProducts = this.querySelector('[data-cro-editor-menu-defaults]:not([hidden])');
        if (editorProducts) {
          this.closeEditorProducts();
        } else if (openCountry) {
          openCountry.open = false;
          openCountry.querySelector('summary').focus();
        } else if (!this.categoryList.hidden) {
          this.closeCategory();
          this.categoryToggle.focus();
        } else if (this.hasAttribute('data-search-open')) {
          this.closeSuggestions(true);
        } else if (this.hasAttribute('data-nav-open') && openMenu && !this.desktop.matches && this.dataset.mobileMenuStyle === 'featured') {
          this.backToMenu(openMenu);
        } else if (this.hasAttribute('data-nav-open')) {
          this.closeMenus();
          this.toggle.focus();
        } else if (openMenu) {
          openMenu.open = false;
          openMenu.querySelector('summary').focus();
        } else return;
        this.handledEscape = true;
        event.preventDefault();
        event.stopPropagation();
      }, options);
      this.addEventListener('keyup', event => {
        // Vision's document handler otherwise moves focus to the cart toggle.
        if (event.key === 'Escape' && this.handledEscape) {
          event.stopPropagation();
          this.handledEscape = false;
        }
      }, options);
      this.backdrop.addEventListener('click', () => this.closeMenus(), options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target) && !this.editorPinned) this.closeMenus();
        this.querySelectorAll('[data-cro-localization][open]').forEach(country => {
          if (!country.contains(event.target)) country.open = false;
        });
      }, options);
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget) && !this.editorPinned) this.closeMenus();
      }, options);
      this.querySelectorAll('.cro-header__actions a').forEach(link => link.addEventListener('click', () => this.closeMenus(), options));
      this.querySelectorAll('[data-cro-account]').forEach(account => {
        let accountOpen = false;
        account.addEventListener('open', () => {
          accountOpen = true;
          this.closeLocalization();
          if (account.dataset.croAccount === 'menu') {
            this.closeCategory();
            this.closeSuggestions();
          } else this.closeMenus();
        }, options);
        account.addEventListener('close', () => { accountOpen = false; }, options);
        account.addEventListener('keydown', event => {
          if (event.key === 'Escape') this.accountEscape = accountOpen;
        }, { ...options, capture: true });
        account.addEventListener('keydown', event => {
          // Keep global consent handlers from cancelling native dialog dismissal.
          if (event.key === 'Escape' && this.accountEscape) event.stopPropagation();
        }, options);
        account.addEventListener('keyup', event => {
          if (event.key === 'Escape' && this.accountEscape) {
            event.stopPropagation();
            this.accountEscape = false;
          }
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        if (this.editorMode) selectedEditorBlock = event.detail?.blockId || null;
        this.editorPinned = this.editorMode;
        this.closeMenus();
        let target = event.target;
        if (this.previewNestedBlock(target)) return;
        if (target.closest('[data-cro-panel]')) {
          this.openSearch();
          this.showDefaults();
          return;
        }
        if (target.matches('[data-cro-editor-menu-defaults]')) {
          target.hidden = false;
          this.updateOverlay();
          return;
        }
        const visibleMenu = this.querySelector(this.desktop.matches ? '.cro-navigation__desktop' : '.cro-navigation__mobile');
        const menuLink = target.closest('.cro-menu-card');
        if (menuLink && visibleMenu && !visibleMenu.contains(target)) {
          target = [...visibleMenu.querySelectorAll('.cro-menu-card')].find(link => link.getAttribute('href') === menuLink.getAttribute('href')) || target;
        }
        if (this.categoryList.contains(target)) {
          this.categoryList.hidden = false;
          this.categoryToggle.setAttribute('aria-expanded', 'true');
          if (!event.detail?.load) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          return;
        }
        if (!this.desktop.matches && target.closest('[data-cro-root-promo]')) {
          this.setAttribute('data-nav-open', '');
          this.toggle.setAttribute('aria-expanded', 'true');
          this.updateOverlay();
          if (!event.detail?.load) target.scrollIntoView({ block: 'nearest' });
          return;
        }
        const menu = target.closest('[data-cro-menu]');
        if (menu) {
          const group = target.closest('[data-cro-group]');
          if (group) menu.querySelectorAll('[data-cro-group]').forEach(item => { item.open = item === group; });
          menu.open = true;
          if (!this.desktop.matches) {
            this.setAttribute('data-nav-open', '');
            this.toggle.setAttribute('aria-expanded', 'true');
          }
          this.updateOverlay();
          if (!event.detail?.load) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }, options);
      this.addEventListener('shopify:block:deselect', event => {
        if (!this.editorMode || event.detail?.blockId !== selectedEditorBlock) return;
        selectedEditorBlock = null;
        this.closeMenus();
      }, options);
      this.desktop.addEventListener('change', () => {
        this.closeMenus();
        if (this.editorMode) this.restoreEditorPreview();
      }, options);
      this.measure = () => {
        const bounds = this.getBoundingClientRect();
        const viewport = window.visualViewport;
        const bottom = Math.max(0, bounds.bottom);
        const available = (viewport ? viewport.height + viewport.offsetTop : window.innerHeight) - bottom;
        document.documentElement.style.setProperty('--header-height', `${bounds.height}px`);
        this.style.setProperty('--cro-header-bottom', `${bottom}px`);
        this.style.setProperty('--cro-available-height', `${Math.max(0, available)}px`);
      };
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this);
      window.addEventListener('scroll', this.measure, { ...options, passive: true });
      window.addEventListener('resize', this.measure, options);
      window.visualViewport?.addEventListener('resize', this.measure, options);
      window.visualViewport?.addEventListener('scroll', this.measure, options);
      this.measure();
      if (this.editorMode) {
        const section = this.closest('.shopify-section') || this;
        section.addEventListener('shopify:section:select', () => {
          selectedEditorBlock = null;
          this.applyEditorPreview();
        }, options);
        section.addEventListener('shopify:section:deselect', () => {
          selectedEditorBlock = null;
          this.editorPinned = false;
          this.closeMenus();
        }, options);
        this.restoreEditorPreview();
      }
    }
    applyNestedConfiguration() {
      const normalize = value => (value || '').trim().toLocaleLowerCase();
      const config = this.querySelector('[data-cro-navigation-config]');
      const menus = [...this.querySelectorAll('[data-cro-menu]')];
      const replace = (container, selector, template, mode) => {
        if (mode === 'inherit' || !container) return;
        container.querySelector(selector)?.remove();
        if (mode !== 'hide' && template) container.append(template.content.cloneNode(true));
      };
      if (config) {
        this.dataset.mobileMenuStyle = config.dataset.mobileMenuStyle;
        this.querySelectorAll('[data-cro-category-layout]').forEach(grid => { grid.dataset.croCategoryLayout = config.dataset.layout; });
        this.querySelectorAll('.cro-mobile-features').forEach(features => {
          features.style.cssText = config.style.cssText;
          if (!features.hasAttribute('data-cro-root-promo')) replace(features, '.cro-menu-products', config.querySelector(':scope > [data-cro-default-menu-products]'), 'custom');
          replace(features, '.cro-menu-promo', config.querySelector(':scope > [data-cro-default-menu-promo]'), 'custom');
        });
      }
      this.querySelectorAll('[data-cro-menu-config]').forEach(area => {
        menus.filter(menu => normalize(menu.dataset.croMenuTitle) === normalize(area.dataset.menuTitle)).forEach(menu => {
          if (area.dataset.layout !== 'inherit') menu.querySelectorAll('[data-cro-category-layout]').forEach(grid => { grid.dataset.croCategoryLayout = area.dataset.layout; });
          const features = menu.querySelector('.cro-mobile-features');
          replace(features, '.cro-menu-products', area.querySelector(':scope > [data-cro-products]'), area.dataset.productsMode);
          replace(features, '.cro-menu-promo', area.querySelector(':scope > [data-cro-promo]'), area.dataset.promoMode);
          if (features && area.dataset.promoMode === 'custom') features.style.cssText = area.style.cssText;
          area.querySelectorAll('[data-cro-image-config]').forEach(image => {
            const template = image.querySelector('template');
            if (!template?.content.querySelector('img')) return;
            menu.querySelectorAll('.cro-menu-card').forEach(card => {
              if (card.getAttribute('href') === image.dataset.url) card.querySelector('.cro-menu-card__image')?.replaceChildren(template.content.cloneNode(true));
            });
          });
          [...area.querySelectorAll('[data-cro-view-config]')].sort((a, b) => Number(Boolean(a.dataset.groupTitle.trim())) - Number(Boolean(b.dataset.groupTitle.trim()))).forEach(view => {
            menu.querySelectorAll('[data-cro-group]').forEach(group => {
              if (!normalize(view.dataset.groupTitle) || normalize(group.querySelector('summary')?.textContent) === normalize(view.dataset.groupTitle)) {
                const grid = group.querySelector('[data-cro-category-layout]');
                if (grid) grid.dataset.croCategoryLayout = view.dataset.layout;
              }
            });
          });
        });
      });
      const search = this.querySelector('[data-cro-search-config]');
      if (!search) return;
      const selectedTypes = new Set((search.dataset.selectedTypes || '').split('\n').map(normalize).filter(Boolean));
      const select = this.querySelector('[data-cro-category] select');
      const list = this.querySelector('[data-cro-category-list]');
      const overrides = [...search.querySelectorAll('[data-cro-search-option]')];
      [...select.options].forEach(option => {
        if (!option.value) return;
        const button = [...list.querySelectorAll('[data-value]')].find(item => item.dataset.value === option.value);
        const visible = search.dataset.typesMode !== 'selected' || selectedTypes.has(normalize(option.value));
        option.disabled = !visible;
        option.hidden = !visible;
        if (button) button.hidden = !visible;
        const override = overrides.find(item => item.dataset.type === option.value);
        if (override?.dataset.label) {
          option.textContent = override.dataset.label;
          if (button) button.textContent = override.dataset.label;
        }
      });
      if (select.selectedOptions[0]?.disabled) select.value = '';
    }
    previewNestedBlock(target) {
      const searchOption = target.closest('[data-cro-search-option]');
      if (searchOption) {
        const button = [...this.categoryList.querySelectorAll('[data-value]')].find(item => item.dataset.value === searchOption.dataset.type);
        button?.click();
        this.openSearch(); this.showDefaults();
        return true;
      }
      if (target.closest('[data-cro-search-module]')) {
        this.openSearch(); this.showDefaults(); return true;
      }
      const area = target.closest('[data-cro-menu-config]');
      if (area) {
        const selector = this.desktop.matches ? '.cro-navigation__desktop' : '.cro-navigation__mobile';
        const visible = this.querySelector(selector) || this;
        const menu = [...visible.querySelectorAll('[data-cro-menu]')].find(item => (item.dataset.croMenuTitle || '').trim().toLocaleLowerCase() === area.dataset.menuTitle.trim().toLocaleLowerCase());
        if (menu) {
          menu.open = true;
          const image = target.closest('[data-cro-image-config]');
          const view = target.closest('[data-cro-view-config]');
          const card = image && [...menu.querySelectorAll('.cro-menu-card')].find(item => item.getAttribute('href') === image.dataset.url);
          const group = card?.closest('[data-cro-group]') || (view && [...menu.querySelectorAll('[data-cro-group]')].find(item => item.querySelector('summary').textContent.trim() === view.dataset.groupTitle));
          if (group) menu.querySelectorAll('[data-cro-group]').forEach(item => { item.open = item === group; });
        }
        if (!this.desktop.matches) { this.setAttribute('data-nav-open', ''); this.toggle.setAttribute('aria-expanded', 'true'); }
        this.updateOverlay(); return true;
      }
      if (target.closest('[data-cro-navigation-module]')) {
        const panel = this.querySelector('[data-cro-editor-menu-defaults]');
        if (panel) { panel.hidden = false; this.updateOverlay(); }
        else this.applyEditorPreview('menu');
        return true;
      }
      return false;
    }
    restoreEditorPreview() {
      this.applyEditorPreview();
      if (!selectedEditorBlock) return;
      const target = [...this.querySelectorAll('[data-shopify-editor-block]')].find(element => {
        try { return JSON.parse(element.dataset.shopifyEditorBlock).id === selectedEditorBlock; }
        catch { return false; }
      });
      target?.dispatchEvent(new CustomEvent('shopify:block:select', { bubbles: true, detail: { blockId: selectedEditorBlock, load: true } }));
    }
    applyEditorPreview(preview = this.dataset.editorPreview) {
      if (!this.editorMode) return;
      this.editorPinned = true;
      this.closeMenus();
      if (preview === 'search') {
        this.openSearch();
        this.showDefaults();
      } else if (preview === 'menu') {
        const selector = this.desktop.matches ? '.cro-navigation__desktop' : '.cro-navigation__mobile';
        const menus = [...(this.querySelector(selector) || this).querySelectorAll('[data-cro-menu]')];
        const title = (this.dataset.editorMenu || '').trim().toLocaleLowerCase();
        const menu = menus.find(item => item.querySelector('summary').textContent.trim().toLocaleLowerCase() === title);
        if (menu) menu.open = true;
        else if (this.desktop.matches && menus[0]) menus[0].open = true;
        if (!this.desktop.matches) {
          this.setAttribute('data-nav-open', '');
          this.toggle.setAttribute('aria-expanded', 'true');
        }
        this.updateOverlay();
      }
    }
    setupLocalization(options) {
      this.querySelectorAll('[data-cro-localization]').forEach(country => {
        country.addEventListener('toggle', () => {
          if (!country.open) return;
          this.querySelectorAll('[data-cro-localization]').forEach(other => { if (other !== country) other.open = false; });
          this.closeCategory();
          this.closeSuggestions();
          if (this.desktop.matches || country.closest('.cro-header__actions')) this.closeNavigation();
        }, options);
        const filter = country.querySelector('[data-cro-country-filter]');
        filter?.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          country.querySelector('[data-cro-country]:not([hidden]) button')?.focus();
        }, options);
        filter?.addEventListener('input', () => {
          const normalize = value => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
          const query = normalize(filter.value.trim());
          const items = [...country.querySelectorAll('[data-cro-country]')];
          items.forEach(item => { item.hidden = !normalize(item.textContent).includes(query); });
          const empty = country.querySelector('[data-cro-country-empty]');
          if (empty) empty.hidden = items.some(item => !item.hidden);
        }, options);
      });
    }
    closeLocalization() {
      this.querySelectorAll('[data-cro-localization]').forEach(country => { country.open = false; });
    }
    backToMenu(menu) {
      if (!menu) return;
      menu.open = false;
      menu.querySelector('summary').focus({ preventScroll: true });
      menu.querySelector('summary').scrollIntoView({ block: 'nearest' });
      this.updateOverlay();
    }
    updateOverlay() {
      this.measure?.();
      const search = this.hasAttribute('data-search-open');
      const mobileMenu = this.hasAttribute('data-nav-open');
      this.backdrop.hidden = this.editorMode || !(search || mobileMenu || this.querySelector('[data-cro-menu][open]'));
      document.documentElement.classList.toggle('cro-overlay-open', !this.editorMode && (search || mobileMenu));
    }
    closeNavigation() {
      clearTimeout(this.menuTimer);
      this.removeAttribute('data-nav-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.querySelectorAll('[data-cro-menu]').forEach(menu => { menu.open = false; });
      this.updateOverlay();
    }
    closeEditorProducts() {
      const editorProducts = this.querySelector('[data-cro-editor-menu-defaults]');
      if (editorProducts) editorProducts.hidden = true;
    }
    closeMenus() {
      this.closeEditorProducts();
      this.closeLocalization();
      this.closeCategory();
      this.closeNavigation();
      this.closeSuggestions();
    }
    disconnectedCallback() {
      this.closeMenus();
      document.body.classList.remove('header-sticky--active');
      this.lifecycle?.abort();
      this.observer?.disconnect();
    }
    setupCategory(options) {
      this.category = this.querySelector('[data-cro-category]');
      this.categoryToggle = this.querySelector('[data-cro-category-toggle]');
      this.categoryList = this.querySelector('[data-cro-category-list]');
      this.categorySelect = this.searchForm.elements['filter.p.product_type'];
      const selectedValue = this.categorySelect.value;
      const collator = new Intl.Collator(document.documentElement.lang || 'de', { sensitivity: 'base', numeric: true });
      const sortCategories = (a, b) => {
        const aValue = a.dataset.value ?? a.value;
        const bValue = b.dataset.value ?? b.value;
        if (!aValue || !bValue) return !aValue ? -1 : 1;
        return collator.compare(a.textContent.trim(), b.textContent.trim());
      };
      const choices = [...this.categoryList.querySelectorAll('[role=option]:not([hidden])')].sort(sortCategories);
      this.categoryList.append(...choices);
      this.categorySelect.append(...[...this.categorySelect.options].sort(sortCategories));
      this.categorySelect.value = selectedValue;
      const sync = () => {
        this.querySelector('[data-cro-category-label]').textContent = this.categorySelect.selectedOptions[0].textContent;
        choices.forEach(choice => choice.setAttribute('aria-selected', String(choice.dataset.value === this.categorySelect.value)));
      };
      const open = () => {
        this.categoryList.hidden = false;
        this.categoryToggle.setAttribute('aria-expanded', 'true');
        choices.find(choice => choice.dataset.value === this.categorySelect.value)?.focus();
      };
      this.categoryToggle.addEventListener('click', () => this.categoryList.hidden ? open() : this.closeCategory(), options);
      this.categoryToggle.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open(); }
      }, options);
      this.categoryList.addEventListener('click', event => {
        const choice = event.target.closest('[data-value]');
        if (!choice) return;
        this.categorySelect.value = choice.dataset.value;
        sync();
        this.closeCategory();
        this.searchInput.focus();
        this.categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      this.categoryList.addEventListener('keydown', event => {
        const index = choices.indexOf(document.activeElement);
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % choices.length;
        if (event.key === 'ArrowUp') next = (index - 1 + choices.length) % choices.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = choices.length - 1;
        if (next !== undefined) { event.preventDefault(); choices[next].focus(); }
      }, options);
      this.category.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.category.contains(event.relatedTarget)) this.closeCategory();
      }, options);
      this.categorySelect.addEventListener('change', sync, options);
      this.addEventListener('click', event => { if (!this.category.contains(event.target)) this.closeCategory(); }, options);
      sync();
    }
    closeCategory() {
      if (!this.categoryList) return;
      this.categoryList.hidden = true;
      this.categoryToggle.setAttribute('aria-expanded', 'false');
    }
    setupSearch(options) {
      this.searchForm = this.querySelector('[data-cro-search]');
      this.searchInput = this.searchForm.elements.q;
      this.results = this.querySelector('[data-cro-results]');
      this.panel = this.querySelector('[data-cro-panel]');
      this.defaults = this.querySelector('[data-cro-defaults]');
      this.clearButton = this.querySelector('[data-cro-clear]');
      this.closeButton = this.querySelector('[data-cro-search-close]');
      this.allResults = this.querySelector('[data-cro-all-results]');
      this.searchStatus = this.querySelector('[data-cro-search-status]');
      this.activeSuggestion = -1;
      this.searchForm.addEventListener('submit', event => {
        if (!this.searchInput.value.trim()) { event.preventDefault(); this.searchInput.focus(); }
      }, options);
      this.scheduleSearch = () => {
        this.cancelRequest();
        this.openSearch();
        this.updateSearchActions();
        if (!this.searchInput.value.trim()) { this.showDefaults(); return; }
        this.results.replaceChildren();
        this.searchStatus.textContent = this.searchStatus.dataset.loading;
        this.results.setAttribute('aria-busy', 'true');
        this.searchTimer = setTimeout(() => this.fetchSuggestions(), 200);
      };
      this.searchInput.addEventListener('input', this.scheduleSearch, options);
      this.searchInput.addEventListener('focus', () => { if (!this.suppressSearchFocus) this.scheduleSearch(); }, options);
      this.searchInput.addEventListener('click', () => { if (!this.hasAttribute('data-search-open')) this.scheduleSearch(); }, options);
      this.searchForm.elements['filter.p.product_type'].addEventListener('change', this.scheduleSearch, options);
      this.clearButton.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchInput.focus();
        this.scheduleSearch();
      }, options);
      this.closeButton.addEventListener('click', () => this.closeSuggestions(true), options);
      this.searchInput.addEventListener('keydown', event => {
        if (this.panel.hidden) return;
        const links = [...this.results.querySelectorAll('[data-cro-suggestion]')];
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && links.length) {
          event.preventDefault();
          this.activeSuggestion = this.activeSuggestion < 0
            ? (event.key === 'ArrowDown' ? 0 : links.length - 1)
            : (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
          links.forEach((link, index) => link.setAttribute('aria-selected', String(index === this.activeSuggestion)));
          this.searchInput.setAttribute('aria-activedescendant', links[this.activeSuggestion].id);
          links[this.activeSuggestion].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else if (event.key === 'Enter' && this.activeSuggestion >= 0 && links[this.activeSuggestion]) {
          event.preventDefault();
          links[this.activeSuggestion].click();
        }
      }, options);
      this.updateSearchActions();
    }
    openSearch() {
      this.closeEditorProducts();
      this.closeLocalization();
      this.closeNavigation();
      this.setAttribute('data-search-open', '');
      this.panel.hidden = false;
      this.results.hidden = false;
      this.closeButton.hidden = false;
      this.searchInput.setAttribute('aria-expanded', 'true');
      this.updateOverlay();
      this.measure?.();
    }
    searchURL(query = this.searchInput.value.trim()) {
      const url = new URL(this.searchForm.action, location.origin);
      url.search = new URLSearchParams(new FormData(this.searchForm)).toString();
      url.searchParams.set('q', query);
      if (!this.searchForm.elements['filter.p.product_type'].value) url.searchParams.delete('filter.p.product_type');
      return url;
    }
    updateSearchActions() {
      const query = this.searchInput.value.trim();
      this.clearButton.hidden = !query;
      this.toggleAttribute('data-has-query', Boolean(query));
      this.allResults.hidden = !query;
      this.allResults.href = this.searchURL().toString();
      const label = this.allResults.querySelector('[data-cro-all-label]') || this.allResults;
      label.textContent = this.allResults.dataset.label.replace('[terms]', query).replace('{{ terms }}', query);
    }
    showDefaults() {
      const categoryDefaults = [...this.querySelectorAll('[data-cro-category-defaults]')].find(template => template.dataset.productType === this.categorySelect.value);
      this.results.replaceChildren((categoryDefaults || this.defaults).content.cloneNode(true));
      const nestedProducts = [...this.querySelectorAll('[data-cro-category-products]')].find(template => template.dataset.productType === this.categorySelect.value);
      if (nestedProducts) this.results.querySelector('.cro-search-grid')?.replaceWith(nestedProducts.content.cloneNode(true));
      const type = this.searchForm.elements['filter.p.product_type'].value;
      this.results.querySelectorAll('[data-cro-product-type]').forEach(card => {
        if (type && card.dataset.croProductType !== type) card.remove();
      });
      this.results.querySelectorAll('[data-cro-product-group]').forEach(group => {
        if (!group.querySelector('[data-cro-product-type]')) group.remove();
      });
      this.results.querySelectorAll('[data-cro-keyword]').forEach(link => {
        link.href = this.searchURL(link.dataset.croKeyword).toString();
      });
      this.searchStatus.textContent = '';
      this.prepareResults();
    }
    prepareResults() {
      this.activeSuggestion = -1;
      this.searchInput.removeAttribute('aria-activedescendant');
      this.results.removeAttribute('aria-busy');
      this.results.querySelectorAll('[data-cro-suggestion]').forEach((link, index) => {
        link.id = `${this.results.id}-${index}`;
        link.setAttribute('aria-selected', 'false');
      });
    }
    cancelRequest() {
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
    }
    closeSuggestions(restoreFocus = false) {
      this.cancelRequest();
      if (!this.panel) return;
      this.panel.hidden = true;
      this.closeButton.hidden = true;
      this.removeAttribute('data-search-open');
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.searchInput.removeAttribute('aria-activedescendant');
      this.activeSuggestion = -1;
      this.updateOverlay();
      if (restoreFocus) {
        this.suppressSearchFocus = true;
        this.searchInput.focus();
        this.suppressSearchFocus = false;
      }
    }
    async fetchSuggestions() {
      this.cancelRequest();
      const controller = new AbortController();
      this.searchRequest = controller;
      this.openSearch();
      this.updateSearchActions();
      const filtered = this.searchForm.elements['filter.p.product_type'].value;
      const url = filtered ? this.searchURL() : new URL(this.searchForm.dataset.suggestUrl, location.origin);
      if (!filtered) {
        url.searchParams.set('q', this.searchInput.value.trim());
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[limit_scope]', 'each');
      }
      url.searchParams.set('section_id', 'cro-search-suggestions');
      try {
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error('Search request failed');
        const html = await response.text();
        if (controller.signal.aborted || !this.isConnected) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cro-suggestions-list]');
        if (!content) throw new Error('Search content unavailable');
        this.results.replaceChildren(...content.children);
        this.searchStatus.textContent = content.dataset.status || '';
        this.prepareResults();
      } catch (error) {
        if (controller.signal.aborted || !this.isConnected) return;
        this.results.replaceChildren();
        this.results.removeAttribute('aria-busy');
        this.searchStatus.textContent = this.searchStatus.dataset.error;
      }
    }
  });
}
